import { styled, css, useTheme } from '@vebox/etui-system';
import { useContext, createElement, useEffect, useCallback, useMemo, Fragment } from 'react';
import { a as _objectSpread2, _ as _objectWithoutProperties, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div } from '@vebox/etui-system-elements';
import '@vebox/etui-colors';
import Spinner from '../Spinner/Spinner.js';
import '../utils-fabc7ae7.js';
import 'react-dom';
import 'element-resize-detector';
import ContainerDimensions from '../ContainerDimensions/ContainerDimensions.js';
import { WidthProvider, Responsive } from 'react-grid-layout';
import DashboardContextProvider, { DashboardContext } from './DashboardContext.js';
import useDashboard from './use-dashboard.js';

const ResponsiveGrid = WidthProvider(Responsive);
const Dashboard = styled(ResponsiveGrid)`
  ${({
  theme
}) => css`
    background: ${theme.palette.common.bg};

    .react-grid-layout {
      position: relative;
      transition: height 200ms ease;
    }
    .react-grid-item {
      transition: all 200ms ease;
      transition-property: left, top;
    }
    .react-grid-item img {
      pointer-events: none;
      user-select: none;
    }
    .react-grid-item.cssTransforms {
      transition-property: transform;
    }
    .react-grid-item.resizing {
      z-index: 1;
      will-change: width, height;
    }

    .react-grid-item.react-draggable-dragging {
      transition: none;
      z-index: 3;
      will-change: transform;
    }

    .react-grid-item.dropping {
      visibility: hidden;
    }

    .react-grid-item.react-grid-placeholder {
      background: red;
      opacity: 0.2;
      transition-duration: 100ms;
      z-index: 2;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      -o-user-select: none;
      user-select: none;
    }

    .react-grid-item > .react-resizable-handle {
      position: absolute;
      width: 20px;
      height: 20px;

      &:hover:after,
      &:active:after {
        border-bottom-color: ${theme.palette.text.primary};
      }
    }

    .react-grid-item > .react-resizable-handle::after {
      content: '';
      position: absolute;
      right: ${theme.spacing.xxs}px;
      bottom: ${theme.spacing.xxs}px;
      width: 0;
      height: 0;
      border-bottom: ${theme.spacing.xs}px solid ${theme.palette.text.secondary};
      border-left: ${theme.spacing.xs}px solid transparent;
    }

    .react-resizable-hide > .react-resizable-handle {
      display: none;
    }

    .react-grid-item > .react-resizable-handle.react-resizable-handle-sw {
      bottom: 0;
      left: 0;
      cursor: sw-resize;
      transform: rotate(90deg);
    }
    .react-grid-item > .react-resizable-handle.react-resizable-handle-se {
      bottom: 0;
      right: 0;
      cursor: se-resize;
    }
    .react-grid-item > .react-resizable-handle.react-resizable-handle-nw {
      top: 0;
      left: 0;
      cursor: nw-resize;
      transform: rotate(180deg);
    }
    .react-grid-item > .react-resizable-handle.react-resizable-handle-ne {
      top: 0;
      right: 0;
      cursor: ne-resize;
      transform: rotate(270deg);
    }
    .react-grid-item > .react-resizable-handle.react-resizable-handle-w,
    .react-grid-item > .react-resizable-handle.react-resizable-handle-e {
      top: 50%;
      margin-top: -10px;
      cursor: ew-resize;
    }
    .react-grid-item > .react-resizable-handle.react-resizable-handle-w {
      left: 0;
      transform: rotate(135deg);
    }
    .react-grid-item > .react-resizable-handle.react-resizable-handle-e {
      right: 0;
      transform: rotate(315deg);
    }

    .react-grid-item > .react-resizable-handle.react-resizable-handle-n,
    .react-grid-item > .react-resizable-handle.react-resizable-handle-s {
      left: 50%;
      margin-left: -10px;
      cursor: ns-resize;
    }

    .react-grid-item > .react-resizable-handle.react-resizable-handle-n {
      top: 0;
      transform: rotate(225deg);
    }

    .react-grid-item > .react-resizable-handle.react-resizable-handle-s {
      bottom: 0;
      transform: rotate(45deg);
    }

    .react-grid-item.react-grid-placeholder {
      background: ${theme.palette.secondary.main};
      opacity: 0.5;
      transition-duration: 150ms;
      z-index: 2;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      -o-user-select: none;
      user-select: none;
    }
  `}
`;

const _excluded = ["Component", "props", "id", "layout"];
function DashboardBase(props) {
  const context = useContext(DashboardContext);
  return !context ? /*#__PURE__*/createElement(DashboardContextProvider, null, /*#__PURE__*/createElement(InnerDashboardBase, props)) : /*#__PURE__*/createElement(InnerDashboardBase, props);
}
function InnerDashboardBase({
  onWidgetsChanged = null,
  widgets: widgetsProp = null,
  testId = 'dashboard',
  height = '100%',
  fixedHeight = undefined,
  columnSizes = null,
  verticalCompact = true,
  spinnerProps = {},
  styles = {}
}) {
  var _widgetComponents$fin, _styles$root;
  const theme = useTheme();
  const [{
    widgets,
    currentFullScreen
  }, dispatch] = useDashboard(onWidgetsChanged);
  useEffect(() => {
    dispatch({
      type: 'LAYOUT_CHANGE',
      widgets: widgetsProp
    });
  }, [dispatch, widgetsProp]);
  const xs = parseInt(theme.breakpoints[0], 10);
  const sm = parseInt(theme.breakpoints[1], 10);
  const md = parseInt(theme.breakpoints[2], 10);
  const lg = parseInt(theme.breakpoints[3], 10);
  const layouts = widgets ? {
    lg: widgets.map(({
      layout,
      id: i
    }) => _objectSpread2({
      i,
      isResizable: !!layout.isResizable
    }, layout))
  } : null;
  const handleLayoutChange = useCallback(widgetsLayoutChanges => {
    const updatedWidgets = widgetsLayoutChanges.map(({
      i,
      x,
      y,
      w,
      h
    }) => {
      const widget = widgets.find(({
        id
      }) => id === i);
      widget.layout = _objectSpread2(_objectSpread2({}, widget.layout), {}, {
        x,
        y,
        w,
        h
      });
      return widget;
    });
    dispatch({
      type: 'LAYOUT_CHANGE',
      widgets: updatedWidgets
    });
  }, [dispatch, widgets]);
  const widgetComponents = useMemo(() => {
    return widgets === null || widgets === void 0 ? void 0 : widgets.map(widget => {
      const {
          Component,
          props,
          id,
          layout
        } = widget,
        rest = _objectWithoutProperties(widget, _excluded);
      const {
        static: isLocked
      } = layout;
      const component = /*#__PURE__*/createElement(ContainerDimensions, null, dimensions => /*#__PURE__*/createElement(Component, _extends({
        id: id,
        isMaximized: currentFullScreen && id === currentFullScreen.id,
        isLocked: isLocked
      }, dimensions, props, rest)));
      return {
        id,
        component
      };
    });
  }, [widgets, currentFullScreen]);
  return currentFullScreen ? (_widgetComponents$fin = widgetComponents.find(({
    id
  }) => id === currentFullScreen.id)) === null || _widgetComponents$fin === void 0 ? void 0 : _widgetComponents$fin.component : /*#__PURE__*/createElement(Div, _extends({
    bg: theme.palette.common.bg,
    height: height,
    testId: testId
  }, styles.root), layouts ? /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Dashboard, {
    onLayoutChange: handleLayoutChange,
    breakpoints: {
      lg,
      md,
      sm,
      xs
    },
    cols: _objectSpread2({
      lg: 12,
      md: 10,
      sm: 6,
      xs: 4
    }, columnSizes),
    className: "layout",
    maxRows: fixedHeight,
    verticalCompact: verticalCompact,
    rowHeight: fixedHeight ? (height || (styles === null || styles === void 0 ? void 0 : (_styles$root = styles.root) === null || _styles$root === void 0 ? void 0 : _styles$root.height) || 0) / fixedHeight - 10 : 150,
    layouts: layouts
  }, widgetComponents === null || widgetComponents === void 0 ? void 0 : widgetComponents.map(({
    component,
    id
  }) => /*#__PURE__*/createElement("div", {
    key: id
  }, component)))) :
  /*#__PURE__*/
  // @ts-ignore
  createElement(Spinner, spinnerProps));
}

export default DashboardBase;
export { InnerDashboardBase };
